import FirestoreBaseService from './firestoreBase';

class MentorService extends FirestoreBaseService {
  constructor() {
    super('mentors');
  }

  /**
   * 全OGを取得
   */
  async fetchAllMentors() {
    try {
      console.log("Fetching mentors...");
      const mentors = await this.getAll();
      console.log(`Found ${mentors.length} mentors`);
      
      // urlParamが存在しない場合はidを使用
      return mentors.map(mentor => ({
        ...mentor,
        urlParam: mentor.urlParam || mentor.id
      }));
    } catch (error) {
      console.error('Error fetching mentors:', error);
      throw this.handleError(error);
    }
  }

  /**
   * 特定のOGをIDで取得
   * @param {string} mentorId - OGID
   */
  async fetchMentorById(mentorId) {
    try {
      console.log(`Fetching mentor with ID: ${mentorId}`);
      const mentor = await this.get(mentorId);
      
      if (!mentor) {
        console.error(`Mentor with ID ${mentorId} not found`);
        throw new Error('Mentor not found');
      }

      console.log("Found mentor data:", mentor);
      return {
        ...mentor,
        urlParam: mentor.urlParam || mentor.id
      };
    } catch (error) {
      console.error('Error fetching mentor:', error);
      throw this.handleError(error);
    }
  }

  /**
   * OGを作成
   * @param {string} mentorId - OGID
   * @param {Object} mentorData - OGデータ
   */
  async createMentor(mentorId, mentorData) {
    try {
      const dataWithUrlParam = {
        ...mentorData,
        urlParam: mentorId
      };
      
      await this.createOrUpdate(mentorId, dataWithUrlParam);
      return { success: true, mentorId };
    } catch (error) {
      console.error('Error creating mentor:', error);
      throw this.handleError(error);
    }
  }

  /**
   * OG情報を更新
   * @param {string} mentorId - OGID
   * @param {Object} updateData - 更新データ
   */
  async updateMentor(mentorId, updateData) {
    try {
      let processedUpdateData = { ...updateData };
      
      // プロフィール画像がある場合はCloud Storageにアップロード
      if (processedUpdateData.profileImageFile) {
        try {
          const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
          const { storage } = await import('../firebase/config');
          
          // Convert base64 data URL to Blob
          const base64Data = processedUpdateData.profileImageFile;
          const response = await fetch(base64Data);
          const blob = await response.blob();
          
          const fileName = `mentor-icons/${mentorId}.jpg`;
          const storageRef = ref(storage, fileName);
          await uploadBytes(storageRef, blob);
          processedUpdateData.アイコンURL = await getDownloadURL(storageRef);
        } catch (error) {
          console.error('Error uploading profile image:', error);
          // Continue without updating the image if upload fails
        }
        
        // Remove the base64 data from the update data
        delete processedUpdateData.profileImageFile;
      }
      
      await this.update(mentorId, processedUpdateData);
      return { success: true, mentorId };
    } catch (error) {
      console.error('Error updating mentor:', error);
      throw this.handleError(error);
    }
  }

  /**
   * 条件に基づいてOGを検索
   * @param {Array} conditions - 検索条件
   * @param {number} limitCount - 取得件数制限
   */
  async searchMentors(conditions = [], limitCount = null) {
    try {
      return await this.getMany(
        conditions,
        [{ field: 'createdAt', direction: 'desc' }],
        limitCount
      );
    } catch (error) {
      console.error('Error searching mentors:', error);
      throw this.handleError(error);
    }
  }

  /**
   * 業界別にOGを取得
   * @param {string} industry - 業界名
   */
  async getMentorsByIndustry(industry) {
    try {
      return await this.getMany([
        { field: '業界', operator: '==', value: industry }
      ]);
    } catch (error) {
      console.error('Error getting mentors by industry:', error);
      throw this.handleError(error);
    }
  }

  /**
   * OGの統計情報を取得
   */
  async getMentorStats() {
    try {
      const mentors = await this.getAll();
      
      // 業界別統計
      const industryStats = {};
      // 職種別統計
      const jobStats = {};
      
      mentors.forEach(mentor => {
        // 業界統計
        const industry = mentor.業界 || '不明';
        industryStats[industry] = (industryStats[industry] || 0) + 1;
        
        // 職種統計
        const job = mentor.職種 || '不明';
        jobStats[job] = (jobStats[job] || 0) + 1;
      });

      return {
        total: mentors.length,
        byIndustry: industryStats,
        byJob: jobStats
      };
    } catch (error) {
      console.error('Error getting mentor stats:', error);
      throw this.handleError(error);
    }
  }

  /**
   * OGの連絡先でOGを検索
   * @param {string} email - メールアドレス
   */
  async findMentorByEmail(email) {
    try {
      const mentors = await this.getMany([
        { field: '連絡先', operator: '==', value: email }
      ]);
      
      return mentors.length > 0 ? mentors[0] : null;
    } catch (error) {
      console.error('Error finding mentor by email:', error);
      throw this.handleError(error);
    }
  }
}

// 既存の関数も互換性のためにエクスポート
const mentorService = new MentorService();
export const fetchAllMentors = mentorService.fetchAllMentors.bind(mentorService);
export const fetchMentorById = mentorService.fetchMentorById.bind(mentorService);
export const updateMentor = mentorService.updateMentor.bind(mentorService);

export default mentorService; 